import React, { useEffect, useState, useMemo, useContext } from "react";
import ReactDOM from "react-dom";
import { AuthContext } from "../../context/AuthContext";
import {
  getFunctionalities,
  deleteFunctionality,
  updateFunctionality,
} from "../../services/functionalityService";
import FunctionalityForm from "./FunctionalityForm";
import ViewFunctionality from "./ViewFunctionality";
import {
  MdVisibility,
  MdEdit,
  MdDelete,
  MdMoreVert,
  MdArrowDropUp,
  MdArrowDropDown,
} from "react-icons/md";
import "../Users.css";
import "../../styles/UserForm.css";

// ===================== Helpers =====================
const safeString = (v) => (v == null ? "" : String(v));

const sortList = (list, key, dir) =>
  [...list].sort((a, b) =>
    dir === "asc"
      ? safeString(a?.[key]).localeCompare(safeString(b?.[key]))
      : safeString(b?.[key]).localeCompare(safeString(a?.[key]))
  );

// ===================== Action Menu =====================
function ActionMenu({ disabled, items }) {
  const btnRef = React.useRef(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (!open || !btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    const left = r.right - 200;
    const top = r.top;
    setPos({ top, left });

    const close = (e) => {
      if (!btnRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close, true);
    return () => document.removeEventListener("mousedown", close, true);
  }, [open]);

  return (
    <>
      <button
        ref={btnRef}
        className="btn btn-sm btn-action-more"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
      >
        <MdMoreVert size={18} />
      </button>
      {open &&
        ReactDOM.createPortal(
          <div
            className="dropdown-menu show shadow-sm actionmenu-portal"
            style={{
              position: "fixed",
              top: pos.top,
              left: pos.left,
              zIndex: 3000,
              minWidth: 180,
            }}
          >
            {(items || []).map((it, i) =>
              it.type === "divider" ? (
                <div key={i} className="dropdown-divider" />
              ) : (
                <button
                  key={i}
                  className="dropdown-item"
                  onClick={() => {
                    setOpen(false);
                    it.onClick?.();
                  }}
                >
                  {it.label}
                </button>
              )
            )}
          </div>,
          document.body
        )}
    </>
  );
}

// ===================== Main Component =====================
export default function FunctionalityList() {
  const { hasPermission } = useContext(AuthContext);

  const [functionalities, setFunctionalities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState("module");
  const [sortDir, setSortDir] = useState("asc");
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const [selected, setSelected] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [viewItem, setViewItem] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [busyId, setBusyId] = useState(null);

  // ✅ Load data
  const load = async () => {
    try {
      setLoading(true);
      const res = await getFunctionalities();
      const data = Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res?.data?.data)
        ? res.data.data
        : [];
      setFunctionalities(data);
    } catch (err) {
      console.error("getFunctionalities failed:", err);
      setFunctionalities([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // ✅ Filter + Sort
  const processed = useMemo(() => {
    let list = [...functionalities];
    const q = search.toLowerCase();
    if (q)
      list = list.filter(
        (f) =>
          safeString(f.name).toLowerCase().includes(q) ||
          safeString(f.module).toLowerCase().includes(q)
      );
    return sortList(list, sortKey, sortDir);
  }, [functionalities, search, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(processed.length / pageSize));
  const pageList = useMemo(() => {
    const start = (page - 1) * pageSize;
    return processed.slice(start, start + pageSize);
  }, [processed, page]);

  const toggleSort = (key) => {
    if (key === sortKey) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const SortIcon = ({ col }) => (
    <span className={`sort-icon ${sortKey === col ? "sort-active" : ""}`}>
      {sortKey === col ? (
        sortDir === "asc" ? (
          <MdArrowDropUp />
        ) : (
          <MdArrowDropDown />
        )
      ) : (
        <MdArrowDropDown />
      )}
    </span>
  );

  const toggleRow = (id) =>
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const allSelected =
    pageList.length > 0 && pageList.every((u) => selected.includes(u.id));

  const toggleAll = () =>
    setSelected((prev) =>
      allSelected
        ? prev.filter((id) => !pageList.some((u) => u.id === id))
        : [...new Set([...prev, ...pageList.map((u) => u.id)])]
    );

  // ✅ Delete single record
  const onDelete = async (f) => {
    if (!window.confirm(`Delete functionality "${f.name}"?`)) return;
    setBusyId(f.id);
    try {
      await deleteFunctionality(f.id);
      await load();
    } finally {
      setBusyId(null);
    }
  };

  // ✅ Bulk Delete
  const handleBulkDelete = async () => {
    if (selected.length === 0) return;
    if (!window.confirm(`Delete ${selected.length} selected functionalities?`))
      return;

    for (const id of selected) {
      await deleteFunctionality(id);
    }
    setSelected([]);
    await load();
  };

  // ✅ Bulk Disable (Deactivate)
  const handleBulkDisable = async () => {
    if (selected.length === 0) return;
    if (!window.confirm(`Deactivate ${selected.length} selected functionalities?`))
      return;

    for (const id of selected) {
      const item = functionalities.find((f) => f.id === id);
      if (item && item.is_active) {
        await updateFunctionality(id, { ...item, is_active: false });
      }
    }
    setSelected([]);
    await load();
  };

  if (loading) return <div className="text-center p-4">Loading…</div>;

  return (
    <div className="container-fluid">
      {/* Header */}
      <div className="users-hero shadow-sm">
        <div>
          <div className="h4 fw-bold mb-1 text-white">
            Functionality Management
          </div>
          <div className="text-white-50 small">
            Manage platform functionalities and menus.
          </div>
        </div>
        {hasPermission("FUNCTIONALITY_MANAGE", "create") && (
          <button
            className="btn-gradient-primary"
            onClick={() => setShowAdd(true)}
          >
            + Add Functionality
          </button>
        )}
      </div>

      {/* 🔍 Search + Bulk Action Toolbar */}
      <div className="users-filter-toolbar d-flex align-items-center justify-content-between">
        <div className="flex-grow-1" style={{ maxWidth: "400px" }}>
          <input
            className="form-control"
            placeholder="Search functionality name or module…"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
        </div>

        {selected.length > 0 && (
          <div className="d-flex align-items-center gap-2 ms-2">
            <button
              className="btn btn-warning btn-sm fw-semibold px-3"
              onClick={handleBulkDisable}
            >
              Disable ({selected.length})
            </button>
            <button
              className="btn btn-danger btn-sm fw-semibold px-3"
              onClick={handleBulkDelete}
            >
              Delete ({selected.length})
            </button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="card shadow-sm">
        <table className="table table-sm users-table mb-0">
          <thead>
            <tr>
              <th style={{ width: 36 }}>
                <input
                  type="checkbox"
                  className="table-checkbox"
                  checked={allSelected}
                  onChange={toggleAll}
                />
              </th>
              <th onClick={() => toggleSort("name")}>
                Name <SortIcon col="name" />
              </th>
              <th onClick={() => toggleSort("code")}>
                Code <SortIcon col="code" />
              </th>
              <th onClick={() => toggleSort("module")}>
                Module <SortIcon col="module" />
              </th>
              <th onClick={() => toggleSort("type")}>
                Type <SortIcon col="type" />
              </th>
              <th>Description</th>
              <th>Status</th>
              <th className="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            {pageList.map((f) => {
              const checked = selected.includes(f.id);
              return (
                <tr
                  key={f.id}
                  className={checked ? "row-selected" : ""}
                  onClick={(e) => {
                    if (e.target.closest("button") || e.target.tagName === "INPUT")
                      return;
                    toggleRow(f.id);
                  }}
                >
                  <td>
                    <input
                      type="checkbox"
                      className="table-checkbox"
                      checked={checked}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => toggleRow(f.id)}
                    />
                  </td>
                  <td>{safeString(f.name)}</td>
                  <td>{safeString(f.code)}</td>
                  <td>{safeString(f.module)}</td>
                  <td>{safeString(f.type)}</td>
                  <td>{safeString(f.description)}</td>
                  <td>
                    <span
                      className={`badge ${
                        f.is_active ? "bg-success" : "bg-secondary"
                      }`}
                    >
                      {f.is_active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td
                    className="text-end d-flex align-items-center justify-content-end gap-2"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      className="btn btn-sm btn-action-view"
                      title="View"
                      onClick={() => setViewItem(f)}
                    >
                      <MdVisibility size={15} />
                    </button>
                    <button
                      className="btn btn-sm btn-action-edit"
                      title="Edit"
                      onClick={() => setEditItem(f)}
                    >
                      <MdEdit size={15} />
                    </button>
                    <ActionMenu
                      disabled={busyId === f.id}
                      items={[
                        {
                          label: (
                            <>
                              <MdDelete className="text-danger" size={16} />{" "}
                              Delete
                            </>
                          ),
                          onClick: () => onDelete(f),
                        },
                      ]}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* Pagination */}
        <div className="users-pagination">
          <button disabled={page === 1} onClick={() => setPage(page - 1)}>
            Prev
          </button>
          {Array.from({ length: totalPages }, (_, i) => (
            <button
              key={i}
              className={page === i + 1 ? "active" : ""}
              onClick={() => setPage(i + 1)}
            >
              {i + 1}
            </button>
          ))}
          <button
            disabled={page === totalPages}
            onClick={() => setPage(page + 1)}
          >
            Next
          </button>
        </div>
      </div>

      {/* ✅ Drawers (uf-modal + uf-card for consistent UI) */}
      {showAdd && (
        <div className="uf-modal">
          <div className="uf-card">
            <FunctionalityForm
              onClose={() => setShowAdd(false)}
              onSuccess={() => {
                setShowAdd(false);
                load();
              }}
            />
          </div>
        </div>
      )}

      {viewItem && (
        <div className="uf-modal">
          <div className="uf-card">
            <ViewFunctionality
              funcData={viewItem}
              onClose={() => setViewItem(null)}
              onEdit={(f) => {
                setViewItem(null);
                setEditItem(f);
              }}
            />
          </div>
        </div>
      )}

      {editItem && (
        <div className="uf-modal">
          <div className="uf-card">
            <FunctionalityForm
              editData={editItem}
              onClose={() => setEditItem(null)}
              onSuccess={() => {
                setEditItem(null);
                load();
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
